import {ChangeDetectionStrategy, Component, inject, input, OnInit, signal, WritableSignal} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {EntityInvestmentViewModel} from '../view-models/entity-investment.view-model';
import {EntityInvestmentService} from '../services/entity-investment.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {EntityInvestmentTransactionViewModel} from '../view-models/entity-investment-transaction.view-model';
import {EntityInvestmentTransactionConverterService} from '../services/entity-investment-transaction-converter.service';
import {Keys} from '@shared/keys';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {BaseEntityDetailsModal} from '../../shared/base-entity-details-modal';
import {EntityTransactionModalComponent} from '../../shared/entity-transaction-modal/entity-transaction-modal.component';

@Component({
    selector: 'app-entity-investment-details-modal',
    templateUrl: './entity-investment-details-modal.component.html',
    styleUrls: ['./entity-investment-details-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ClientsideDataService],
    imports: [EntityTransactionModalComponent]
})
export class EntityInvestmentDetailsModalComponent
  extends BaseEntityDetailsModal<EntityInvestmentViewModel, EntityInvestmentTransactionViewModel>
  implements OnInit {

  readonly modalDataInput = input.required<EntityInvestmentViewModel>();

  readonly entityInvestment: WritableSignal<EntityInvestmentViewModel> = signal(undefined as unknown as EntityInvestmentViewModel);

  private readonly entityInvestmentService = inject(EntityInvestmentService);
  private readonly entityInvestmentTransactionConverterService = inject(EntityInvestmentTransactionConverterService);

  ngOnInit() {
    this.entityInvestment.set(this.modalDataInput());
    this.initializeModal(this.entityInvestment(), 'EntityInvestmentDetailsModal');
  }

  protected populateColumns(): TableTemplateColumnConfig[] {
    return [
      {
        title: 'Date',
        dataProperty: 'date',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Left,
        width: '27%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Type',
        dataProperty: 'type',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '23%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Description',
        dataProperty: 'description',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '30%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount',
        dataProperty: 'amount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '20%',
        dataFocusable: false,
        headingFocusable: true
      }
    ];
  }

  protected getSearchFields(): Keys<EntityInvestmentTransactionViewModel> {
    return ['type', 'description'];
  }

  protected getSortedTable(): SortedTable {
    return SortedTable.Investments;
  }

  protected getTransactionSortedTable(): SortedTable {
    return SortedTable.InvestmentTransactions;
  }

  protected override shouldSkipLoad(entity: EntityInvestmentViewModel): boolean {
    return !entity.portfolioNumber || !entity.id;
  }

  protected loadTransactions(entity: EntityInvestmentViewModel): Observable<EntityInvestmentTransactionViewModel[]> {
    return this.entityInvestmentService.getEntityInvestmentTransactions(entity.entityCode, entity.id, entity.portfolioNumber).pipe(
      map(transactionList => this.entityInvestmentTransactionConverterService.convert(transactionList))
    );
  }
}
